/**
 * Dynamic block OBJECTS parser — DYN-1.2.
 *
 * Decodes the ACDBBLOCK* / ACDBBLOCKVISIBILITYSTATE records produced by
 * AutoCAD's dynamic block authoring into a typed
 * `DynamicBlockMetadata` instance. Non-Tier-1 classes (e.g.
 * ACDBBLOCKPOLARPARAMETER from DYN-2) are preserved under
 * `meta.futureExtensions.rawObjects` so a subsequent writer run can
 * re-emit them verbatim.
 *
 * Paired with `dxfBridge.dynamic.writer.ts` (symmetric emitter) and
 * `dxfBridge.dynamic.shared.ts` (group-lookup helpers).
 */
import type {
  AssocVariable,
  DxfDynamicBlockObject,
} from "@dxf-ai-editor/dxf-io";

import {
  deriveEvaluationEdges,
  type DependencyEdge,
  type DynamicAction,
  type DynamicBlockMetadata,
  type DynamicGrip,
  type DynamicParameter,
  type FlipAction,
  type FlipParameter,
  type GeometricConstraint,
  type LinearParameter,
  type LinearValueSet,
  type LookupTable,
  type MoveAction,
  type StretchAction,
  type VisibilityParameter,
  type VisibilityState,
  type BasePointParameter,
  type UserParameter,
  validateDynamicMetadata,
} from "./dynamicBlock";
import {
  GRIP_CLASSES,
  isGripClass,
  parseGrip,
} from "./dxfBridge.dynamic.parser.grips";
import { parseUserParameter } from "./dxfBridge.dynamic.parser.userParam";

// Phase 5.1.d — DynamicAspect registry migration. Importing the aspect
// modules registers them as a side effect (`registerAspect(...)` at
// module load). The registry-driven dispatch below replaces the direct
// `parseGrip` / `parseUserParameter` calls when the legacy flag is off.
import { findAspect } from "./dxfBridge.dynamic.aspect";
import "./dxfBridge.dynamic.aspect.grip";
import "./dxfBridge.dynamic.aspect.userParam";
import "./dxfBridge.dynamic.aspect.bpt";

/**
 * Phase 5.1 emergency rollback flag. Set to `true` (e.g. via a hotfix
 * patch) to force the dispatcher to skip the `DynamicAspect` registry
 * and use the legacy direct-call paths. Defaults to `false` (registry
 * is the production path). Drop in PR 5.1.f once the registry has been
 * stable across one Codex review cycle on all 5 aspects.
 */
const DYNAMIC_ASPECT_LEGACY = false;
import {
  canonicalDynamicBlockClass,
  collectGroups,
  decodeInlineStatesFromBvpGroups,
  firstGroup,
  firstInt,
  firstNumber,
  parsePointAt,
} from "./dxfBridge.dynamic.shared";
import {
  parseBlockPropertiesTable,
  TIER2_ACTION_CLASSES,
  TIER2_PARAMETER_CLASSES,
  TIER2_TABLE_CLASSES,
  tryParseTier2Action,
  tryParseTier2Parameter,
} from "./dxfBridge.dynamic.parser.tier2";

// Re-export the canonical-class helper so consumers that already depend
// on the top-level parser can reach it without adding a second import.
// The canonical source lives in `dxfBridge.dynamic.shared.ts` (which is
// already imported by every dynamic-parser sibling module).
export { canonicalDynamicBlockClass } from "./dxfBridge.dynamic.shared";
import {
  TIER3_ACTION_CLASSES,
  TIER3_GEOMETRIC_CONSTRAINT_CLASSES,
  TIER3_PARAMETER_CLASSES,
  tryParseTier3Action,
  tryParseTier3GeometricConstraint,
  tryParseTier3Parameter,
} from "./dxfBridge.dynamic.parser.tier3";

// ═══════════════════════════════════════════════════════════════════════════
// Per-class decoders
// ═══════════════════════════════════════════════════════════════════════════

function parseLinearParameter(obj: DxfDynamicBlockObject): LinearParameter {
  const groups = obj.rawGroups;
  const name = firstGroup(groups, 1)?.trim() ?? `linear_${obj.handle}`;
  const basePoint = parsePointAt(groups, 10, 20);
  const secondPoint = parsePointAt(groups, 11, 21);
  const defaultDistance = firstNumber(groups, 140) ?? 0;
  const currentDistance = firstNumber(groups, 141) ?? defaultDistance;
  const valueSetModeRaw = firstInt(groups, 1071) ?? 0;
  const mode: LinearValueSet["mode"] =
    valueSetModeRaw === 1
      ? "increment"
      : valueSetModeRaw === 2
        ? "list"
        : "none";
  const increment = firstNumber(groups, 142) ?? 0;
  const min = firstNumber(groups, 143);
  const max = firstNumber(groups, 144);
  const listRaw = collectGroups(groups, 145).map(Number.parseFloat);
  const listValues = listRaw.filter(Number.isFinite);

  const valueSet: LinearValueSet = {
    mode,
    increment,
    listValues,
  };
  if (min !== undefined) valueSet.min = min;
  if (max !== undefined) valueSet.max = max;

  return {
    kind: "linear",
    id: obj.handle,
    name,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    basePoint,
    secondPoint,
    defaultDistance,
    currentDistance,
    valueSet,
  };
}

function parseFlipParameter(obj: DxfDynamicBlockObject): FlipParameter {
  const groups = obj.rawGroups;
  const name = firstGroup(groups, 1)?.trim() ?? `flip_${obj.handle}`;
  const axisStart = parsePointAt(groups, 10, 20);
  const axisEnd = parsePointAt(groups, 11, 21);
  const defaultValue = (firstInt(groups, 280) ?? 0) !== 0;
  const currentValue = (firstInt(groups, 281) ?? (defaultValue ? 1 : 0)) !== 0;

  return {
    kind: "flip",
    id: obj.handle,
    name,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 282) ?? 0) === 1,
    showGrip: (firstInt(groups, 283) ?? 1) !== 0,
    axisStart,
    axisEnd,
    defaultValue,
    currentValue,
    flipLabel: firstGroup(groups, 300)?.trim() || "Flip",
    baseLabel: firstGroup(groups, 301)?.trim() || "Base",
  };
}

function parseVisibilityParameter(
  obj: DxfDynamicBlockObject,
  statesByOwner: Map<string, VisibilityState[]>,
  stateHandleToId: Map<string, string>,
): VisibilityParameter {
  const groups = obj.rawGroups;
  // DYN-4 Phase 7 — AutoCAD 2018+ writes the user-facing name via group
  // 300 (AcDbBlockElement subclass). The ezdxf / synthetic shape uses
  // group 1. Prefer 300 so the АБИ1.dxf ibeam visibility surfaces as
  // "Состояние видимости" rather than the handle-based fallback.
  const name =
    firstGroup(groups, 300)?.trim() ??
    firstGroup(groups, 1)?.trim() ??
    `visibility_${obj.handle}`;
  // AutoCAD 2018+ stores the visibility anchor at 1010/1020/1030
  // (AcDbBlock1PtParameter OCS point). The synthetic writer uses 10/20.
  // Try 1010 first, then 10 for fallback.
  const position1010 = parsePointAt(groups, 1010, 1020);
  const position =
    position1010.x !== 0 || position1010.y !== 0
      ? position1010
      : parsePointAt(groups, 10, 20);

  // DYN-4 Phase 7 — AutoCAD 2018+ emits state names INLINE via group
  // 303 (one entry per state, in authoring order) on the parameter
  // record itself, in addition to (or instead of) child
  // BLOCKVISIBILITYSTATE records. Inline is authoritative when
  // present — it mirrors the behaviour of the pass-2 visibility
  // decoder in `dxfBridge.dynamic.visibility.ts` and keeps the ibeam
  // block showing "Торец / Сбоку / Сверху" instead of the bogus
  // single "Default" fallback.
  const inlineNames = collectGroups(groups, 303)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const seen = new Set<string>();
  const inlineDeduped: string[] = [];
  for (const n of inlineNames) {
    if (!seen.has(n)) {
      seen.add(n);
      inlineDeduped.push(n);
    }
  }

  // P5 visibility fix — AutoCAD 2018+ encodes per-state invisible
  // entity handles INLINE within the BVP record (303/94/332/95/333
  // sequences) without child BLOCKVISIBILITYSTATE records. The shared
  // `decodeInlineStatesFromBvpGroups` helper (kept in
  // `dxfBridge.dynamic.shared.ts` for re-use by the pass-2 un-prefixed
  // fallback) walks the groups once and returns state-name →
  // invisible-entity-handle list. Without this pass-1 path produced
  // synthesized states with empty `invisibleEntityIds: []` for the
  // ibeam ("Двутавр") block, so the evaluator had nothing to hide.
  const inlineStateEntities = decodeInlineStatesFromBvpGroups(groups);

  const childStates = statesByOwner.get(obj.handle) ?? [];
  const childByName = new Map<string, VisibilityState>();
  for (const s of childStates) {
    if (!childByName.has(s.name)) childByName.set(s.name, s);
  }

  let states: VisibilityState[];
  if (inlineDeduped.length > 0) {
    states = inlineDeduped.map((n, i) => {
      const inlineEntities = inlineStateEntities.get(n);
      const child = childByName.get(n);
      if (inlineEntities && inlineEntities.length > 0) {
        // Encoding A (AutoCAD 2018+ inline) — entity handles come
        // from the BVP record itself. When a child state with
        // matching NAME exists its handle is preferred as id for
        // stability with downstream references.
        return {
          id: child ? child.id : `${obj.handle}-inline-${i}`,
          name: n,
          invisibleEntityIds: [...inlineEntities],
        };
      }
      if (child) {
        // Encoding A+B (ezdxf-style with child records) — no inline
        // entities, take everything from the child state.
        return child;
      }
      // Encoding C (inline-only, no entity list) — synthesize id;
      // invisibleEntityIds stays empty (AutoCAD-legal "dropdown works,
      // no geometry toggle").
      return {
        id: `${obj.handle}-inline-${i}`,
        name: n,
        invisibleEntityIds: [],
      };
    });
  } else if (childStates.length > 0) {
    states = childStates;
  } else {
    states = [
      // Visibility parameters must have at least one state. If the
      // DXF is malformed we synthesize a default to keep the schema
      // valid; validateDynamicMetadata still flags inconsistencies.
      {
        id: `${obj.handle}-default`,
        name: "Default",
        invisibleEntityIds: [],
      },
    ];
  }

  const defaultHandle = firstGroup(groups, 340)?.trim() ?? "";
  const currentHandle = firstGroup(groups, 341)?.trim() ?? defaultHandle;
  const defaultStateId = stateHandleToId.get(defaultHandle) ?? states[0]!.id;
  const currentStateId = stateHandleToId.get(currentHandle) ?? defaultStateId;

  return {
    kind: "visibility",
    id: obj.handle,
    name,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    position,
    states,
    defaultStateId,
    currentStateId,
  };
}

function parseBasePointParameter(
  obj: DxfDynamicBlockObject,
): BasePointParameter {
  const groups = obj.rawGroups;
  const name = firstGroup(groups, 1)?.trim() ?? `basepoint_${obj.handle}`;
  return {
    kind: "basepoint",
    id: obj.handle,
    name,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: false,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    position: parsePointAt(groups, 10, 20),
  };
}

function parseVisibilityState(obj: DxfDynamicBlockObject): VisibilityState {
  const groups = obj.rawGroups;
  const stateName = firstGroup(groups, 2)?.trim() ?? `state_${obj.handle}`;
  const handles = collectGroups(groups, 340).map((v) => v.trim());
  const invisibleEntityIds = handles.filter((h) => h.length > 0);
  return {
    id: obj.handle,
    name: stateName,
    invisibleEntityIds,
  };
}

function parseMoveAction(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): MoveAction {
  const groups = obj.rawGroups;
  const parameterHandle =
    firstGroup(groups, 330)?.trim() ?? firstGroup(groups, 340)?.trim() ?? "";
  const parameterId = handleToParamId.get(parameterHandle) ?? parameterHandle;
  const selectionHandles = collectGroups(groups, 340)
    .slice(1) // first 340 is the parameter handle
    .map((v) => v.trim())
    .filter((h) => h.length > 0);
  return {
    kind: "move",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? "",
    parameterId,
    selectionEntityIds: selectionHandles,
    distanceMultiplier: firstNumber(groups, 440) ?? 1,
    angleOffset: firstNumber(groups, 441) ?? 0,
  };
}

function parseStretchAction(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): StretchAction {
  const groups = obj.rawGroups;
  const parameterHandle =
    firstGroup(groups, 330)?.trim() ?? firstGroup(groups, 340)?.trim() ?? "";
  const parameterId = handleToParamId.get(parameterHandle) ?? parameterHandle;
  const selectionHandles = collectGroups(groups, 340)
    .slice(1)
    .map((v) => v.trim())
    .filter((h) => h.length > 0);
  const corner1 = parsePointAt(groups, 10, 20);
  const corner2 = parsePointAt(groups, 11, 21);
  return {
    kind: "stretch",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? "",
    parameterId,
    selectionEntityIds: selectionHandles,
    distanceMultiplier: firstNumber(groups, 440) ?? 1,
    angleOffset: firstNumber(groups, 441) ?? 0,
    crossingFrame: { corner1, corner2 },
    stretchDirection: firstNumber(groups, 50) ?? 0,
  };
}

function parseFlipAction(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): FlipAction {
  const groups = obj.rawGroups;
  const parameterHandle =
    firstGroup(groups, 330)?.trim() ?? firstGroup(groups, 340)?.trim() ?? "";
  const parameterId = handleToParamId.get(parameterHandle) ?? parameterHandle;
  const selectionHandles = collectGroups(groups, 340)
    .slice(1)
    .map((v) => v.trim())
    .filter((h) => h.length > 0);
  return {
    kind: "flip",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? "",
    parameterId,
    selectionEntityIds: selectionHandles,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Scope collector + top-level entry point
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Group every ACDBBLOCK* OBJECTS record that belongs to the given
 * BLOCK_RECORD. The binding is transitive: parameters/actions sit under
 * an ACDBBLOCKREPRESENTATION whose ownerHandle points at the
 * BLOCK_RECORD (or its extension dictionary). Since different AutoCAD
 * builds vary in the exact chain we accept any dynamic-block object
 * whose ownerHandle is either the BLOCK_RECORD handle directly, one of
 * the block's own parameter handles, or is listed in the
 * representation's group references.
 */
function collectObjectsForBlock(
  blockRecordHandle: string,
  allObjects: DxfDynamicBlockObject[],
): DxfDynamicBlockObject[] {
  const out: DxfDynamicBlockObject[] = [];
  const accepted = new Set<string>();
  const queue: string[] = [blockRecordHandle];
  while (queue.length > 0) {
    const owner = queue.shift()!;
    for (const obj of allObjects) {
      if (accepted.has(obj.handle)) continue;
      if (obj.ownerHandle === owner) {
        accepted.add(obj.handle);
        out.push(obj);
        queue.push(obj.handle);
      }
    }
  }
  return out;
}

/**
 * Reconstruct `DynamicBlockMetadata` from the OBJECTS belonging to a
 * single BLOCK_RECORD. Returns `undefined` when no relevant objects are
 * found (i.e. the block is static).
 *
 * @param assocVariables Pre-scanned `Map<handle, AssocVariable>` produced
 *                       by `collectAssocVariables`. Optional — when
 *                       omitted, BLOCKUSERPARAMETER records synthesise
 *                       defaults (value = 0, type = "unknown"), which is
 *                       acceptable for legacy unit-test fixtures that
 *                       don't ship associative variables.
 */
export function buildDynamicBlockMetadata(
  blockRecordHandle: string,
  allObjects: DxfDynamicBlockObject[],
  assocVariables?: Map<string, AssocVariable>,
): DynamicBlockMetadata | undefined {
  if (!blockRecordHandle) return undefined;
  const scoped = collectObjectsForBlock(blockRecordHandle, allObjects);
  if (scoped.length === 0) return undefined;
  const resolvedAssoc = assocVariables ?? new Map<string, AssocVariable>();

  // First pass — visibility states (children of visibility parameters).
  const statesByOwner = new Map<string, VisibilityState[]>();
  const stateHandleToId = new Map<string, string>();
  for (const obj of scoped) {
    if (canonicalDynamicBlockClass(obj.type) !== "ACDBBLOCKVISIBILITYSTATE") continue;
    const state = parseVisibilityState(obj);
    const owner = obj.ownerHandle ?? "";
    if (!statesByOwner.has(owner)) statesByOwner.set(owner, []);
    statesByOwner.get(owner)!.push(state);
    stateHandleToId.set(obj.handle, state.id);
  }

  // Second pass — tables (lookup + block properties). We decode these
  // before parameters because Lookup parameters need to reference their
  // tables by id (group 340 in the parameter maps to the table's handle).
  const lookupTables: LookupTable[] = [];
  const tableHandleToId = new Map<string, string>();
  let blockPropertiesTable: DynamicBlockMetadata["blockPropertiesTable"];
  for (const obj of scoped) {
    if (!TIER2_TABLE_CLASSES.has(canonicalDynamicBlockClass(obj.type))) continue;
    // `handleToParamId` is not populated yet on this pass — pass an empty
    // map. The bridge layer can refine column.parameterId later if needed.
    const parsed = parseBlockPropertiesTable(obj, new Map());
    // Determine whether this is a BTABLE or a lookup table. BTABLE rows
    // are exposed via `blockPropertiesTable.entries`; if the table has
    // entries with a type label, prefer the BTABLE representation and
    // skip the lookup-table shape. Otherwise treat it as a lookup table.
    if (parsed.blockPropertiesTable) {
      blockPropertiesTable = parsed.blockPropertiesTable;
    } else {
      lookupTables.push(parsed.lookupTable);
      tableHandleToId.set(obj.handle, parsed.lookupTable.id);
    }
  }

  // Third pass — parameters (Tier 1 + Tier 2).
  const parameters: DynamicParameter[] = [];
  const handleToParamId = new Map<string, string>();
  const unsupportedObjects: DxfDynamicBlockObject[] = [];
  for (const obj of scoped) {
    const canonical = canonicalDynamicBlockClass(obj.type);
    switch (canonical) {
      case "ACDBBLOCKLINEARPARAMETER": {
        const p = parseLinearParameter(obj);
        parameters.push(p);
        handleToParamId.set(obj.handle, p.id);
        break;
      }
      case "ACDBBLOCKFLIPPARAMETER": {
        const p = parseFlipParameter(obj);
        parameters.push(p);
        handleToParamId.set(obj.handle, p.id);
        break;
      }
      case "ACDBBLOCKVISIBILITYPARAMETER": {
        const p = parseVisibilityParameter(obj, statesByOwner, stateHandleToId);
        parameters.push(p);
        handleToParamId.set(obj.handle, p.id);
        break;
      }
      case "ACDBBLOCKBASEPOINTPARAMETER": {
        const p = parseBasePointParameter(obj);
        parameters.push(p);
        handleToParamId.set(obj.handle, p.id);
        break;
      }
      case "ACDBBLOCKUSERPARAMETER": {
        // DYN-4 Phase 3 Fix 6 — resolve the user parameter's associated
        // variable (pre-scanned by the bridge) so its name / value /
        // expression survive the parser.
        //
        // Phase 5.1.d — dispatch through the DynamicAspect registry
        // (default) so the user-parameter aspect owns this branch. The
        // legacy direct-call path remains as the rollback escape hatch.
        const aspect = DYNAMIC_ASPECT_LEGACY ? undefined : findAspect(canonical);
        const p =
          aspect?.parse !== undefined
            ? (aspect.parse(obj, canonical, resolvedAssoc) as UserParameter)
            : parseUserParameter(obj, resolvedAssoc);
        parameters.push(p);
        handleToParamId.set(obj.handle, p.id);
        break;
      }
      default: {
        // Tier 2 dispatch.
        if (TIER2_PARAMETER_CLASSES.has(canonical)) {
          const p = tryParseTier2Parameter(obj, tableHandleToId);
          if (p) {
            parameters.push(p);
            handleToParamId.set(obj.handle, p.id);
          }
        } else if (TIER3_PARAMETER_CLASSES.has(canonical)) {
          // Tier 3 dispatch (Alignment + Aligned/Radial/Diameter constraints).
          const p = tryParseTier3Parameter(obj);
          if (p) {
            parameters.push(p);
            handleToParamId.set(obj.handle, p.id);
          }
        }
        break;
      }
    }
  }

  // Fourth pass — actions (Tier 1 + Tier 2 + Tier 3).
  const actions: DynamicAction[] = [];
  for (const obj of scoped) {
    const canonical = canonicalDynamicBlockClass(obj.type);
    switch (canonical) {
      case "ACDBBLOCKMOVEACTION":
        actions.push(parseMoveAction(obj, handleToParamId));
        break;
      case "ACDBBLOCKSTRETCHACTION":
        actions.push(parseStretchAction(obj, handleToParamId));
        break;
      case "ACDBBLOCKFLIPACTION":
        actions.push(parseFlipAction(obj, handleToParamId));
        break;
      default: {
        if (TIER2_ACTION_CLASSES.has(canonical)) {
          const a = tryParseTier2Action(obj, handleToParamId);
          if (a) actions.push(a);
        } else if (TIER3_ACTION_CLASSES.has(canonical)) {
          const a = tryParseTier3Action(obj, handleToParamId);
          if (a) actions.push(a);
        }
        break;
      }
    }
  }

  // Fourth-and-a-half pass — Tier 3 geometric constraints. These live in
  // ACDBBLOCK2DGEOMETRICCONSTRAINT records whose subclass marker identifies
  // the concrete kind (parallel / perpendicular / coincident / …). Unknown
  // subclass markers are preserved as raw under futureExtensions.rawObjects
  // by the generic unsupported-object handling below.
  const geometricConstraints: GeometricConstraint[] = [];
  for (const obj of scoped) {
    if (!TIER3_GEOMETRIC_CONSTRAINT_CLASSES.has(canonicalDynamicBlockClass(obj.type)))
      continue;
    const parsed = tryParseTier3GeometricConstraint(obj);
    if (parsed) geometricConstraints.push(parsed);
  }

  // Fourth-and-three-quarters pass — DYN-4 Phase 4b grip typing. Grips
  // are OBJECTS records (BLOCKLINEARGRIP / BLOCKVISIBILITYGRIP /
  // BLOCKPROPERTIESTABLEGRIP / …) distinct from the parameters they
  // reference. Previous phases left them in `futureExtensions.rawObjects`
  // for round-trip fidelity; Phase 4b promotes them to typed records on
  // `metadata.grips[]` so the UI can render them on the canvas. The
  // auxiliary `BLOCKGRIPLOCATIONCOMPONENT` / `ACDB_DYNAMICBLOCKPROXYNODE`
  // records stay in rawObjects — they carry expression sub-components
  // and proxy footers we do not decode at this phase.
  // Phase 5.1.d — dispatch grips through the DynamicAspect registry.
  // The grip aspect (`dxfBridge.dynamic.aspect.grip.ts`) wraps the
  // existing `parseGrip` function verbatim. The legacy direct call
  // remains as the rollback escape hatch.
  const grips: DynamicGrip[] = [];
  for (const obj of scoped) {
    const canonical = canonicalDynamicBlockClass(obj.type);
    if (!isGripClass(canonical)) continue;
    const aspect = DYNAMIC_ASPECT_LEGACY ? undefined : findAspect(canonical);
    grips.push(
      aspect?.parse !== undefined
        ? (aspect.parse(obj, canonical, undefined) as DynamicGrip)
        : parseGrip(obj, canonical),
    );
  }

  // Fifth pass — preservation of objects we could not decode. Tier 1 +
  // Tier 2 shapes are captured as typed members above; anything else
  // (future tiers, vendor extensions, orphan records) round-trips
  // through `futureExtensions.rawObjects`.
  const DECODED_CLASSES = new Set<string>([
    // Tier 1
    "ACDBBLOCKLINEARPARAMETER",
    "ACDBBLOCKFLIPPARAMETER",
    "ACDBBLOCKVISIBILITYPARAMETER",
    "ACDBBLOCKBASEPOINTPARAMETER",
    "ACDBBLOCKVISIBILITYSTATE",
    "ACDBBLOCKMOVEACTION",
    "ACDBBLOCKSTRETCHACTION",
    "ACDBBLOCKFLIPACTION",
    // DYN-4 Phase 3 Fix 6 — user parameter (backed by ACDBASSOCVARIABLE).
    "ACDBBLOCKUSERPARAMETER",
    // Tier 2
    ...TIER2_PARAMETER_CLASSES,
    ...TIER2_ACTION_CLASSES,
    ...TIER2_TABLE_CLASSES,
    // Tier 3
    ...TIER3_PARAMETER_CLASSES,
    ...TIER3_ACTION_CLASSES,
    // DYN-4 Phase 4b Fix 7 — grip OBJECTS classes.
    ...GRIP_CLASSES,
  ]);
  // Track decoded geometric constraint handles so we don't double-preserve
  // them as raw when their subclass marker matched a known kind.
  const decodedGeomHandles = new Set<string>(
    geometricConstraints.map((c) => c.id),
  );
  for (const obj of scoped) {
    const canonical = canonicalDynamicBlockClass(obj.type);
    if (DECODED_CLASSES.has(canonical)) continue;
    if (TIER3_GEOMETRIC_CONSTRAINT_CLASSES.has(canonical)) {
      // Only treat as unsupported if we couldn't decode it.
      if (!decodedGeomHandles.has(obj.handle)) {
        unsupportedObjects.push(obj);
      }
      continue;
    }
    unsupportedObjects.push(obj);
  }

  const edges: DependencyEdge[] = deriveEvaluationEdges(actions);

  const meta: DynamicBlockMetadata = {
    version: 1,
    parameters,
    actions,
    evaluationGraph: { edges, chainGroups: [] },
    lookupTables,
    // DYN-3 Batch D: geometric constraints are parsed from
    // ACDBBLOCK2DGEOMETRICCONSTRAINT objects whose subclass marker
    // identifies the concrete kind. Unknown markers preserve as raw
    // so round-trip keeps them intact.
    geometricConstraints,
    // DYN-4 Phase 4b Fix 7 — typed grip nodes extracted from
    // BLOCK<KIND>GRIP OBJECTS records. Coordinates and backing
    // parameter references are surfaced so canvas rendering can consume
    // them directly; the raw groups are preserved for verbatim
    // round-trip writes.
    grips,
    ...(blockPropertiesTable ? { blockPropertiesTable } : {}),
    futureExtensions: {
      rawObjects: unsupportedObjects,
    },
  };

  // Validation — do not reject; surface as a console.warn so unit tests
  // can assert absence of noise on well-formed inputs.
  const warnings = validateDynamicMetadata(meta);
  if (warnings.length > 0 && typeof console !== "undefined") {
    console.warn(
      `[dxfBridge.dynamic] block ${blockRecordHandle}: ${warnings.length} validation warnings`,
    );
  }

  return meta;
}
